import { BrandThemeProvider, DEFAULT_BRAND_THEME } from '@adholics/ui';
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ChatbotWidget } from './components/ChatbotWidget';
import { CommandPalette } from './components/CommandPalette';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { AuthProvider, useAuth } from './lib/auth';
import { ADMIN_BRANDS_ROUTE_SEGMENT, ADMIN_BRAND_DETAIL_ROUTE_SEGMENT } from './lib/brand-wire';
import { ErrorBoundary } from './lib/error-boundary';
import { StaffProvider } from './lib/staff';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';

// Eagerly bundled — every signed-in user lands on these.
import { HomePage } from './routes/HomePage';

// Code-split the rest. Each lazy() boundary becomes its own chunk so the
// initial SPA bundle only ships login + home. Admin chunks (the heaviest)
// don't load until a platform admin actually opens /admin.
const SignupPage = lazy(() =>
  import('./routes/SignupPage').then((m) => ({ default: m.SignupPage })),
);
const Verify2faPage = lazy(() =>
  import('./routes/Verify2faPage').then((m) => ({ default: m.Verify2faPage })),
);
const AwaitingApprovalPage = lazy(() =>
  import('./routes/AwaitingApprovalPage').then((m) => ({ default: m.AwaitingApprovalPage })),
);
const SetPasswordPage = lazy(() =>
  import('./routes/SetPasswordPage').then((m) => ({ default: m.SetPasswordPage })),
);
const AccountPage = lazy(() =>
  import('./routes/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const AgencyPage = lazy(() =>
  import('./routes/AgencyPage').then((m) => ({ default: m.AgencyPage })),
);
const BillingPage = lazy(() =>
  import('./routes/BillingPage').then((m) => ({ default: m.BillingPage })),
);
const EmbedPage = lazy(() => import('./routes/EmbedPage').then((m) => ({ default: m.EmbedPage })));
const SupportRouter = lazy(() =>
  import('./routes/support/SupportRouter').then((m) => ({ default: m.SupportRouter })),
);

const DashboardListPage = lazy(() =>
  import('./dashboard/DashboardListPage').then((m) => ({ default: m.DashboardListPage })),
);
const DashboardConfigRenderer = lazy(() =>
  import('./dashboard/DashboardConfigRenderer').then((m) => ({
    default: m.DashboardConfigRenderer,
  })),
);
const DashboardLayout = lazy(() => import('./dashboard/DashboardLayout.jsx'));

// Admin chunk: pulls in react-grid-layout, dnd-kit, recharts builder UIs.
// Only loads when a platform admin lands on /admin.
const AdminShell = lazy(() =>
  import('./admin/AdminShell').then((m) => ({ default: m.AdminShell })),
);
const AdminOverview = lazy(() =>
  import('./admin/AdminOverview').then((m) => ({ default: m.AdminOverview })),
);
const AdminOnboardingPage = lazy(() =>
  import('./admin/onboarding/AdminOnboardingPage').then((m) => ({
    default: m.AdminOnboardingPage,
  })),
);
const AdminBrandsPage = lazy(() =>
  import('./admin/AdminBrandsPage').then((m) => ({ default: m.AdminBrandsPage })),
);
const AdminBrandDetailPage = lazy(() =>
  import('./admin/AdminBrandDetailPage').then((m) => ({ default: m.AdminBrandDetailPage })),
);
const AdminDataSourcesPage = lazy(() =>
  import('./admin/AdminDataSourcesPage').then((m) => ({ default: m.AdminDataSourcesPage })),
);
const AdminNamingPage = lazy(() =>
  import('./admin/AdminNamingPage').then((m) => ({ default: m.AdminNamingPage })),
);
const AdminAliasesPage = lazy(() =>
  import('./admin/AdminAliasesPage').then((m) => ({ default: m.AdminAliasesPage })),
);
const AdminPacingPage = lazy(() =>
  import('./admin/AdminPacingPage').then((m) => ({ default: m.AdminPacingPage })),
);
const AdminCachePage = lazy(() =>
  import('./admin/AdminCachePage').then((m) => ({ default: m.AdminCachePage })),
);
const AdminDashboardsPage = lazy(() =>
  import('./admin/AdminDashboardsPage').then((m) => ({ default: m.AdminDashboardsPage })),
);
const AdminAuditPage = lazy(() =>
  import('./admin/AdminAuditPage').then((m) => ({ default: m.AdminAuditPage })),
);
const AdminUsagePage = lazy(() =>
  import('./admin/AdminUsagePage').then((m) => ({ default: m.AdminUsagePage })),
);
const AdminSignupsPage = lazy(() =>
  import('./routes/AdminSignupsPage').then((m) => ({ default: m.AdminSignupsPage })),
);
const AdminSupportTicketsPage = lazy(() =>
  import('./admin/AdminSupportTicketsPage').then((m) => ({ default: m.AdminSupportTicketsPage })),
);
const AdminDashboardBuildPage = lazy(() =>
  import('./admin/AdminDashboardBuildPage').then((m) => ({ default: m.AdminDashboardBuildPage })),
);

function PageFallback() {
  return (
    <main className="auth">
      <div className="card">
        <p className="muted">Loading…</p>
      </div>
    </main>
  );
}

export function App() {
  return (
    <BrandThemeProvider
      // TODO: Replace with the brand's stored theme from brands.theme_json once the api lane's migration lands.
      value={DEFAULT_BRAND_THEME}
    >
      <AuthProvider>
        <StaffProvider>
          <TooltipProvider delayDuration={250}>
            <BrowserRouter>
              <Toaster position="top-right" richColors closeButton />
              <ChatbotMount />
              <CommandPalette />
              <Suspense fallback={<PageFallback />}>
                <RouteErrorBoundary>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/verify-2fa" element={<Verify2faPage />} />
                    <Route path="/awaiting-approval" element={<AwaitingApprovalPage />} />
                    <Route path="/embed/:tokenId" element={<EmbedPage />} />
                    <Route path="/support/*" element={<SupportRouter />} />
                    <Route
                      path="/set-password"
                      element={
                        <Protected>
                          <SetPasswordPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/billing"
                      element={
                        <Protected>
                          <BillingPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/account"
                      element={
                        <Protected>
                          <AccountPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/agency"
                      element={
                        <Protected>
                          <AgencyPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        <Protected>
                          <HomePage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/analytics"
                      element={
                        <Protected>
                          <DashboardListPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/analytics/_legacy/mg"
                      element={
                        <Protected>
                          <DashboardLayout />
                        </Protected>
                      }
                    />
                    <Route
                      path="/analytics/:slug"
                      element={
                        <Protected>
                          <DashboardConfigRenderer />
                        </Protected>
                      }
                    />
                    {/* Full-page builder lives outside AdminShell so it gets the whole viewport. */}
                    <Route
                      path="/admin/dashboards/:id/build"
                      element={
                        <Protected>
                          <AdminDashboardBuildPage />
                        </Protected>
                      }
                    />
                    {/* Admin console — gated server-side via /admin/me check inside AdminShell. */}
                    <Route
                      path="/admin"
                      element={
                        <Protected>
                          <AdminShell />
                        </Protected>
                      }
                    >
                      <Route index element={<AdminOverview />} />
                      <Route path="onboarding" element={<AdminOnboardingPage />} />
                      <Route path={ADMIN_BRANDS_ROUTE_SEGMENT} element={<AdminBrandsPage />} />
                      <Route
                        path={ADMIN_BRAND_DETAIL_ROUTE_SEGMENT}
                        element={<AdminBrandDetailPage />}
                      />
                      <Route path="data-sources" element={<AdminDataSourcesPage />} />
                      <Route path="naming" element={<AdminNamingPage />} />
                      <Route path="aliases" element={<AdminAliasesPage />} />
                      <Route path="pacing" element={<AdminPacingPage />} />
                      <Route path="cache" element={<AdminCachePage />} />
                      <Route path="dashboards" element={<AdminDashboardsPage />} />
                      <Route path="signups" element={<AdminSignupsPage />} />
                      <Route path="support" element={<AdminSupportTicketsPage />} />
                      <Route path="audit" element={<AdminAuditPage />} />
                      <Route path="usage" element={<AdminUsagePage />} />
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </RouteErrorBoundary>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </StaffProvider>
      </AuthProvider>
    </BrandThemeProvider>
  );
}

function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={`${location.pathname}${location.search}`}>{children}</ErrorBoundary>
  );
}

/** Mount the chatbot widget only for signed-in users — the bot greets
 *  with "you're signed in" framing, so showing it on /login or /signup
 *  is confusing. */
function ChatbotMount() {
  const { ready, me } = useAuth();
  const location = useLocation();
  if (!ready || !me?.user) return null;
  // Hide on the dashboard renderer to avoid covering chart corners.
  if (location.pathname.startsWith('/analytics/') && location.pathname !== '/analytics') {
    return null;
  }
  return <ChatbotWidget />;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { ready, sessionKey, me } = useAuth();
  const location = useLocation();
  // We have a refresh-cookie session (sessionKey just minted) but /auth/me
  // hasn't returned yet. Don't bounce to /login mid-bootstrap — wait it out.
  if (!ready || (sessionKey && !me)) {
    return <PageFallback />;
  }
  if (!me?.user) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }
  return <>{children}</>;
}
